import { useEffect, useRef } from "react";
import { EMAIL_CODE_LENGTH } from "@pagemates/shared";
import { signIn } from "@pagemates/shared/copy";

const txt = signIn.codeBoxes;

/*
 * Six boxes for an emailed code (PRD "Email addresses and email sign-in"). Typing moves on, Backspace moves
 * back, and pasting a whole code into any box fills them all. The first box carries
 * autocomplete="one-time-code", so phones offer the code from the email. `onComplete` fires with all six.
 */
export function CodeBoxes({ value, onChange, onComplete, disabled, autoFocus, label = txt.codeFromTheEmail }: {
  value: string; onChange: (v: string) => void; onComplete?: (v: string) => void; disabled?: boolean; autoFocus?: boolean; label?: string;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length: EMAIL_CODE_LENGTH }, (_, i) => value[i] ?? "");
  useEffect(() => { if (autoFocus) refs.current[Math.min(value.length, EMAIL_CODE_LENGTH - 1)]?.focus(); }, [autoFocus]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (next: string) => {
    const clean = next.replace(/\D/g, "").slice(0, EMAIL_CODE_LENGTH);
    onChange(clean);
    refs.current[Math.min(clean.length, EMAIL_CODE_LENGTH - 1)]?.focus();
    if (clean.length === EMAIL_CODE_LENGTH) onComplete?.(clean);
  };
  return (
    <fieldset className="code-boxes" disabled={disabled}>
      <legend className="sr-only">{label}</legend>
      {digits.map((d, i) => (
        <input key={i} ref={(el) => { refs.current[i] = el; }} className={`digit${i === value.length ? " next" : ""}`} value={d}
          inputMode="numeric" autoComplete={i === 0 ? "one-time-code" : "off"} aria-label={txt.digitOf(i + 1, EMAIL_CODE_LENGTH)}
          onChange={(e) => {
            const typed = e.target.value.replace(/\D/g, "");
            if (typed.length > 1) { set(value.slice(0, i) + typed); return; } // a paste, or the phone's suggested code
            set(value.slice(0, i) + typed + value.slice(i + 1));
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !d && i > 0) { e.preventDefault(); set(value.slice(0, i - 1) + value.slice(i)); refs.current[i - 1]?.focus(); }
            if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
            if (e.key === "ArrowRight" && i < EMAIL_CODE_LENGTH - 1) refs.current[i + 1]?.focus();
          }}
          onPaste={(e) => { e.preventDefault(); set(value.slice(0, i) + e.clipboardData.getData("text")); }} />
      ))}
    </fieldset>
  );
}
